import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHead } from "@/pages/admin/AdminLayout";
import { api, formatApiError } from "@/lib/api";

const FIELDS = [
  ["name", "Clinic name"], ["city", "City"], ["address", "Address"], ["phone", "Phone"],
  ["email", "Booking notification email"], ["opening_hours", "Opening hours"], ["image", "Cover photo URL"],
];

export default function Settings() {
  const [f, setF] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  useEffect(() => {
    api.get("/admin/clinic").then((r) => setF(r.data));
    api.get("/disciplines").then((r) => setDisciplines(r.data));
  }, []);
  if (!f) return null;
  const save = async (e) => {
    e.preventDefault();
    const cutoff = f.cancellation_cutoff_hours ?? 24;
    if (cutoff === "" || Number(cutoff) < 0 || Number(cutoff) > 168) { toast.error("Cancellation cutoff must be between 0 and 168 hours"); return; }
    const body = Object.fromEntries([...FIELDS.map(([k]) => [k, f[k] || ""]), ["description", f.description || ""], ["discipline", f.discipline],
      ["cancellation_cutoff_hours", Number(cutoff)]]);
    try { setF(await api.put("/admin/clinic", body).then((r) => r.data)); toast.success("Clinic updated"); } catch (e2) { toast.error(formatApiError(e2)); }
  };
  return (
    <div className="fade-up max-w-3xl">
      <PageHead eyebrow={f.approved ? "Live on marketplace" : "Awaiting approval"} title="Clinic settings" />
      <form onSubmit={save} className="rounded-2xl bg-card border border-border p-6 space-y-5" data-testid="clinic-settings-form">
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={`space-y-1.5 ${k === "image" || k === "address" ? "sm:col-span-2" : ""}`}>
              <Label>{l}</Label>
              <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}-input`} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label>Main treatment</Label>
            <Select value={f.discipline} onValueChange={(v) => setF({ ...f, discipline: v })}>
              <SelectTrigger data-testid="settings-discipline-select"><SelectValue /></SelectTrigger>
              <SelectContent>{disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Free cancellation cutoff (hours before appointment)</Label>
          <Input type="number" min={0} max={168} className="w-40" value={f.cancellation_cutoff_hours ?? 24}
            onChange={(e) => setF({ ...f, cancellation_cutoff_hours: e.target.value })} data-testid="settings-cutoff-hours-input" />
          <p className="text-xs text-muted-foreground">Patients cancelling earlier than this get an automatic full refund. Later cancellations aren't refunded. Bookings you cancel are always refunded.</p>
        </div>
        <div className="space-y-1.5">
          <Label>About the clinic</Label>
          <Textarea rows={5} value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="settings-description-input" />
        </div>
        {f.image && <img src={f.image} alt="Cover preview" className="h-40 w-full object-cover rounded-xl" />}
        <Button type="submit" className="rounded-full" data-testid="settings-save-button">Save changes</Button>
      </form>
    </div>
  );
}
